// =============================================================================
// Origin UI — src/pages/leave/LieuComponents.tsx
// Shared building blocks for day-in-lieu claims (Oct 2026):
//   LieuStatusPill, LieuApprovalProgress, LieuTimeline, LieuClaimFormModal
// =============================================================================

import { useEffect, useState } from 'react';
import {
  lieuApi,
  lieuStatusTone,
  formatLieuDate,
  LIEU_EVENT_LABELS,
  type LieuClaim,
  type LieuDateCheck,
} from '@/services/lieuApi';
import styles from './lieu.module.css';

// ─── Status pill ───

export function LieuStatusPill({ claim }: { claim: LieuClaim }) {
  const tone = lieuStatusTone(claim);
  return (
    <span className={styles.pill} style={{ background: tone.bg, color: tone.fg }}>
      <span className={styles.pillDot} style={{ background: tone.dot }} />
      {claim.statusLabel}
    </span>
  );
}

// ─── Approval progress (line manager + HR) ───

function stepState(required: boolean, decision: string | null, claimStatus: string) {
  if (!required) return 'skip';
  if (decision === 'Approved') return 'done';
  if (decision === 'Rejected') return 'no';
  if (claimStatus === 'Pending') return 'wait';
  return 'idle';
}

export function LieuApprovalProgress({ claim }: { claim: LieuClaim }) {
  const fmt = (d: string | null) => d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '';

  const rows = [
    {
      key: 'mgr',
      title: 'Line manager',
      state: stepState(claim.managerApprovalRequired, claim.managerDecision, claim.status),
      sub: !claim.managerApprovalRequired
        ? 'Not required (no line manager on record)'
        : claim.managerDecision
          ? `${claim.managerDecision} by ${claim.managerDecisionByName} · ${fmt(claim.managerDecisionAt)}`
          : claim.status === 'Pending'
            ? `Waiting for ${claim.managerName || 'line manager'}`
            : '—',
    },
    {
      key: 'hr',
      title: 'HR',
      state: stepState(claim.hrApprovalRequired, claim.hrDecision, claim.status),
      sub: !claim.hrApprovalRequired
        ? 'Not required (HR team member — line manager approves)'
        : claim.hrDecision
          ? `${claim.hrDecision} by ${claim.hrDecisionByName} · ${fmt(claim.hrDecisionAt)}`
          : claim.status === 'Pending'
            ? 'Waiting for an HR Admin'
            : '—',
    },
  ];

  const icon = (s: string) => s === 'done' ? '✓' : s === 'no' ? '✕' : s === 'wait' ? '…' : '–';
  const iconClass = (s: string) =>
    s === 'done' ? styles.iconDone : s === 'no' ? styles.iconNo : s === 'wait' ? styles.iconWait : styles.iconSkip;

  return (
    <div className={styles.progress}>
      {rows.map(r => (
        <div key={r.key} className={styles.progressRow}>
          <span className={`${styles.progressIcon} ${iconClass(r.state)}`}>{icon(r.state)}</span>
          <div className={styles.progressText}>
            <div className={styles.progressTitle}>{r.title}</div>
            <div className={styles.progressSub}>{r.sub}</div>
          </div>
        </div>
      ))}
      {claim.status === 'Approved' && claim.creditYear && (
        <div className={styles.infoNote}>
          {claim.days === 0.5 ? 'Half a day' : `${claim.days} day${claim.days === 1 ? '' : 's'}`} credited to the TOIL balance for {claim.creditYear}.
        </div>
      )}
    </div>
  );
}

// ─── Timeline ───

export function LieuTimeline({ claim }: { claim: LieuClaim }) {
  if (claim.events.length === 0) return null;
  return (
    <ul className={styles.timeline}>
      {claim.events.map((e, i) => (
        <li key={i} className={styles.timelineItem}>
          <div className={styles.timelineTitle}>{LIEU_EVENT_LABELS[e.eventType] || e.eventType}</div>
          <div className={styles.timelineMeta}>
            {e.actorName} · {new Date(e.createdAt).toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
          </div>
          {e.comment && e.eventType !== 'Submitted' && <div className={styles.timelineComment}>{e.comment}</div>}
        </li>
      ))}
    </ul>
  );
}

// ─── Claim form (new claim + resubmit) ───

function todayIso(): string {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
}

export function LieuClaimFormModal({
  existing,
  onClose,
  onSaved,
}: {
  existing?: LieuClaim | null;      // set → resubmit mode
  onClose: () => void;
  onSaved: (claim: LieuClaim) => void;
}) {
  const isResubmit = !!existing;
  const [workedDate, setWorkedDate] = useState(existing ? existing.workedDate.split('T')[0] : '');
  const [isHalfDay, setIsHalfDay] = useState(existing ? existing.days === 0.5 : false);
  const [reason, setReason] = useState(existing?.reason ?? '');
  const [note, setNote] = useState('');
  const [check, setCheck] = useState<LieuDateCheck | null>(null);
  const [checking, setChecking] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Live date validation (weekend / public holiday for the employee's office)
  useEffect(() => {
    if (!workedDate) { setCheck(null); return; }
    let cancelled = false;
    setChecking(true);
    lieuApi.checkDate(workedDate, existing?.lieuDayClaimId)
      .then(res => {
        if (cancelled) return;
        setCheck(res);
        if (res.halfDayOnly) setIsHalfDay(true);
      })
      .catch(err => { if (!cancelled) setCheck({ isClaimable: false, halfDayOnly: false, message: err.message || 'Could not check this date' }); })
      .finally(() => { if (!cancelled) setChecking(false); });
    return () => { cancelled = true; };
  }, [workedDate, existing?.lieuDayClaimId]);

  const reasonOk = reason.trim().length >= 10;
  const canSave = !!workedDate && !!check?.isClaimable && reasonOk && !saving && !checking;

  async function handleSave() {
    try {
      setSaving(true);
      setError('');
      const payload = { workedDate, isHalfDay, reason: reason.trim() };
      const saved = isResubmit
        ? await lieuApi.resubmit(existing!.lieuDayClaimId, { ...payload, resubmissionNote: note.trim() || undefined })
        : await lieuApi.create(payload);
      onSaved(saved);
    } catch (err: any) {
      setError(err.message || 'Could not save the claim');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={e => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className={styles.modalHeader}>
          <div>
            <h3 className={styles.modalTitle}>{isResubmit ? 'Update & Resubmit Claim' : 'Claim a Day in Lieu'}</h3>
            <p className={styles.modalSub}>
              For a weekend day or public holiday you worked. Your line manager and HR both approve it.
            </p>
          </div>
          <button className={styles.closeBtn} onClick={onClose} aria-label="Close">×</button>
        </div>

        <div className={styles.modalBody}>
          {isResubmit && existing?.decisionComments && (
            <div className={styles.rejectionNote}>
              <strong>Rejected by {existing.rejectedByName}:</strong> {existing.decisionComments}
            </div>
          )}

          <div className={styles.field}>
            <label className={styles.label} htmlFor="lieu-date">Date worked</label>
            <input
              id="lieu-date"
              type="date"
              className={styles.input}
              value={workedDate}
              max={todayIso()}
              onChange={e => setWorkedDate(e.target.value)}
            />
            {checking && <span className={styles.hint}>Checking date…</span>}
            {!checking && check && (
              <div className={check.isClaimable ? styles.checkOk : styles.checkBad}>
                <span aria-hidden="true">{check.isClaimable ? '✓' : '!'}</span>
                <span>{check.message}</span>
              </div>
            )}
          </div>

          <div className={styles.field}>
            <span className={styles.label}>Amount</span>
            <div className={styles.segmented} role="radiogroup" aria-label="Full or half day">
              <button
                type="button"
                className={`${styles.segment} ${!isHalfDay ? styles.segmentActive : ''}`}
                onClick={() => setIsHalfDay(false)}
                disabled={!!check?.halfDayOnly}
                aria-pressed={!isHalfDay}
              >
                Full day
              </button>
              <button
                type="button"
                className={`${styles.segment} ${isHalfDay ? styles.segmentActive : ''}`}
                onClick={() => setIsHalfDay(true)}
                aria-pressed={isHalfDay}
              >
                Half day
              </button>
            </div>
          </div>

          <div className={styles.field}>
            <label className={styles.label} htmlFor="lieu-reason">What work did you do?</label>
            <textarea
              id="lieu-reason"
              className={styles.textarea}
              rows={4}
              maxLength={2000}
              value={reason}
              onChange={e => setReason(e.target.value)}
              placeholder="e.g. Covered the Saturday cargo nomination for the Fujairah lifting with Ops — 09:00–17:00."
            />
            <span className={styles.charCount}>
              {reason.trim().length < 10 ? `At least 10 characters (${reason.trim().length}/10)` : `${reason.length}/2000`}
            </span>
          </div>

          {isResubmit && (
            <div className={styles.field}>
              <label className={styles.label} htmlFor="lieu-note">What have you changed or added? (optional)</label>
              <textarea
                id="lieu-note"
                className={styles.textarea}
                rows={2}
                maxLength={2000}
                value={note}
                onChange={e => setNote(e.target.value)}
                placeholder="e.g. Added the hours worked and who asked me to cover."
              />
            </div>
          )}

          {error && <div className={styles.checkBad}><span aria-hidden="true">!</span><span>{error}</span></div>}
        </div>

        <div className={styles.modalFooter}>
          <button className={styles.secondaryBtn} onClick={onClose} disabled={saving}>Cancel</button>
          <button className={styles.primaryBtn} onClick={handleSave} disabled={!canSave}>
            {saving ? 'Submitting…' : isResubmit ? 'Resubmit Claim' : 'Submit Claim'}
          </button>
        </div>
      </div>
    </div>
  );
}

export { formatLieuDate };
